import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ModuleStyle,
    ModuleContainer
} from '../components/TCFWritingStyles';

import {
    countWords,
    taskDefinitions
} from '../services/utils';

import {
    TaskSelectionScreen,
    ExamScreen,
    ConfirmationModal
} from '../components/EssayWritingComponents';
import TCFResultsPage from './TCFResultsPage';
import Footer from '../components/Footer';
import Menu from '../components/Navbar';
import MyToast from '../components/MyToast';
import MySpinner from '../components/MySpinner';
import { selectRandomsQuestions } from '../services/api';
import { useAuth } from '../Contexts/AuthContext';
import './TCFWritingModule.css'
import { sendMailWithNodemailer } from "../services/api";

// Nouveau composant pour l'abonnement requis
const SubscriptionRequired = ({ resetModule }) => (
    <div className="subscription-required-container">
        <div className="subscription-required-card">
            <h3>Abonnement requis</h3>
            <p>Vous avez atteint la limite d'examens disponibles avec votre compte actuel.</p>
            <p>Pour continuer à utiliser cette fonctionnalité, veuillez souscrire à l'une de nos offres d'abonnement.</p>
            <div className="subscription-buttons">
                <button
                    onClick={() => window.location.href = "/offres"}
                    className="primary-button"
                >
                    Voir les offres
                </button>
                <button
                    onClick={resetModule}
                    className="secondary-button"
                >
                    Retour
                </button>
            </div>
        </div>
    </div>
);

const TCFWritingModule = () => {
    // États de l'application
    const [stage, setStage] = useState('checking'); // Nouvel état initial 'checking', puis 'selection', 'exam', 'completed', 'subscription_required'
    const [selectedExamType, setSelectedExamType] = useState(null);
    const [currentTask, setCurrentTask] = useState('task1');
    const [timeLeft, setTimeLeft] = useState(0);
    const [isTimerRunning, setIsTimerRunning] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [confirmationAction, setConfirmationAction] = useState(null);
    const [inProgress, setinProgress] = useState(false);
    const [spinnerMsg, setSpinnerMsg] = useState('');
    const [openToast, setOpenToast] = useState({
        open: false,
        msg: '',
        severity: ''
    });

    // Nouveaux états pour le contrôle d'accès
    const [canSubmit, setCanSubmit] = useState(false);
    const [remainingSubmissions, setRemainingSubmissions] = useState(0);
    const [subscriptionSource, setSubscriptionSource] = useState('');
    const [subscriptionID, setSubscriptionID] = useState('');

    const { user } = useAuth();
    const [subjects, setSubjects] = useState([]);
    const [jobId, setJobId] = useState(null);
    const [evaluationResults, setEvaluationResults] = useState(null);
    const navigate = useNavigate();

    // États pour les réponses et les instructions
    const [responses, setResponses] = useState({
        task1: '',
        task2: '',
        task3: ''
    });
    const [instructionsCollapsed, setInstructionsCollapsed] = useState({
        task1: false,
        task2: false,
        task3: false
    });

    // Références pour le timer
    const timerRef = useRef(null);
    const checkStatusIntervalRef = useRef(null);

    // Modifier cet useEffect dans TCFWritingModule
    useEffect(() => {
        // Ne rien faire si user est null (encore en chargement)
        if (user === null) {
            return;
        }

        // Si user est undefined (déterminé comme non connecté), rediriger
        if (!user) {
            navigate('/connexion', { state: { returnUrl: window.location.pathname } });
            return;
        }

        // Sinon, vérifier l'éligibilité
        checkSubmissionEligibility();
    }, [user]);

    //console.log(subjects);


    // Fonction pour vérifier si l'utilisateur peut soumettre
    const checkSubmissionEligibility = async () => {
        try {
            setinProgress(true);

            const myHeaders = new Headers();
            myHeaders.append("Content-Type", "application/json");

            const requestOptions = {
                method: 'GET',
                headers: myHeaders,
                redirect: 'follow'
            };

            const response = await fetch(`https://fwabl598k2.execute-api.us-west-1.amazonaws.com/dev/remainingtopics/speaking-corrections/can-submit/${user.uid}`, requestOptions);
            const result = await response.json();

            setCanSubmit(result.canSubmit);
            setRemainingSubmissions(result.remaining);
            setSubscriptionSource(result.source);

            if (result.subscriptionID) {
                setSubscriptionID(result.subscriptionID);
            }

            setinProgress(false);

            if (result.canSubmit) {
                setStage('selection');
            } else {
                setStage('subscription_required');
            }
        } catch (error) {
            setinProgress(false);
            setOpenToast({
                open: true,
                msg: "Une erreur s'est produite lors de la vérification de votre éligibilité",
                severity: "error"
            });
            console.error('Error checking eligibility:', error);
        }
    };

    // Fonction pour décrémenter le compteur après une soumission réussie
    const decrementRemainingSubmissions = async () => {
        try {
            const myHeaders = new Headers();
            myHeaders.append("Content-Type", "application/json");

            const raw = JSON.stringify({
                "userID": user.uid
            });

            const requestOptions = {
                method: 'POST',
                headers: myHeaders,
                body: raw,
                redirect: 'follow'
            };

            const response = await fetch("https://fwabl598k2.execute-api.us-west-1.amazonaws.com/dev/remainingtopics/speaking-corrections/use", requestOptions);
            const result = await response.json();

            if (result.success) {
                setRemainingSubmissions(result.remaining);
            } else {
                console.error('Failed to decrement remaining submissions:', result.message);
            }
        } catch (error) {
            console.error('Error decrementing remaining submissions:', error);
        }
    };

    // Démarrer l'examen
    const startExam = async () => {
        try {
            setSpinnerMsg('')
            setinProgress(true);
            let criteria = [];
            if (selectedExamType == "complete") criteria = [
                {
                    "questionNumber": 1,
                    "subjectName": "EE"
                },
                {
                    "questionNumber": 2,
                    "subjectName": "EE"
                },
                {
                    "questionNumber": 3,
                    "subjectName": "EE"
                }
            ]
            if (selectedExamType == "task1") criteria = [
                {
                    "questionNumber": 1,
                    "subjectName": "EE"
                }
            ]
            if (selectedExamType == "task2") criteria = [
                {
                    "questionNumber": 2,
                    "subjectName": "EE"
                }
            ]
            if (selectedExamType == "task3") criteria = [
                {
                    "questionNumber": 3,
                    "subjectName": "EE"
                }
            ]

            const subjectRep = await selectRandomsQuestions({
                "criteria": criteria
            })

            setSubjects(subjectRep.data.results || []);
            setinProgress(false);
            let duration = 0;
            if (selectedExamType === 'complete') {
                duration = 60 * 60; // 60 minutes en secondes
                setCurrentTask('task1');
            } else {
                duration = taskDefinitions[selectedExamType].duration * 60;
                setCurrentTask(selectedExamType);
            }

            setTimeLeft(duration);
            setIsTimerRunning(true);
            setStage('exam');

        } catch (error) {
            setinProgress(false);
            setOpenToast({
                open: true,
                msg: "Une erreur s'est produite, veuillez réessayer ultérieurement",
                severity: "error"
            })
        }
    };

    // Gérer le timer
    useEffect(() => {
        if (isTimerRunning && timeLeft > 0) {
            timerRef.current = setInterval(() => {
                setTimeLeft(prev => {
                    if (prev <= 1) {
                        clearInterval(timerRef.current);
                        setIsTimerRunning(false);
                        completeExam();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [isTimerRunning]);

    // Vérifier le statut du job
    useEffect(() => {
        if (jobId && stage === 'completed') {
            checkStatusIntervalRef.current = setInterval(checkJobStatus, 3000);
        }

        return () => {
            if (checkStatusIntervalRef.current) {
                clearInterval(checkStatusIntervalRef.current);
            }
        };
    }, [jobId]);

    // Fonction pour vérifier le statut du job
    const checkJobStatus = async () => {
        try {
            const myHeaders = new Headers();
            myHeaders.append("Content-Type", "application/json");

            const requestOptions = {
                method: 'GET',
                headers: myHeaders,
                redirect: 'follow'
            };

            const response = await fetch(`https://fwabl598k2.execute-api.us-west-1.amazonaws.com/dev/tcfagent/check-task-status?userId=${user.uid}&jobId=${jobId}`, requestOptions);
            const result = await response.json();

            if (result.status === "completed") {
                setEvaluationResults(result.results);
                setinProgress(false);
                clearInterval(checkStatusIntervalRef.current);

                // Décrémenter le compteur une fois que les résultats sont disponibles
                await decrementRemainingSubmissions();

                sendMailWithNodemailer({
                    from: 'services@exams-tcfcanada.com',
                    to_email: `examstcfcanada@gmail.com`,
                    from_name: 'EXAMS TCF CANADA WEB',
                    subject: "SOUMISSION D'EXPRESSION ECRITE - " + selectedExamType?.toUpperCase(),
                    text: `Un candidat vient de soumettre son examen: \n\n ${user.uid}`
                });
            }
        } catch (error) {
            console.error('Error checking job status:', error);
        }
    };

    // Changer de tâche
    const switchTask = (taskId) => {
        setCurrentTask(taskId);
    };

    // Mettre à jour la réponse
    const updateResponse = (taskId, text) => {
        setResponses(prev => ({
            ...prev,
            [taskId]: text
        }));
    };

    // Basculer l'affichage des instructions
    const toggleInstructions = (taskId) => {
        setInstructionsCollapsed(prev => ({
            ...prev,
            [taskId]: !prev[taskId]
        }));
    };

    // Afficher la confirmation avant de terminer l'examen
    const showConfirmation = (action) => {
        setConfirmationAction(action);
        setShowConfirmationModal(true);
    };

    // Soumettre les réponses à l'API
    const submitResponses = async () => {
        try {
            setinProgress(true);
            setOpenToast({
                open: true,
                msg: `Merci de patienter pendant que notre assistant intelligent corrige votre examen. \nCela peut prendre quelques minutes. `,
                severity: "info"
            })

            // Préparer les données pour l'API
            const inputData = {
                subjects: []
            };

            // Mapper les tâches aux numéros de question
            const taskToQuestionNumber = {
                'task1': 1,
                'task2': 2,
                'task3': 3
            };

            // Remplir les sujets selon le type d'examen sélectionné
            if (selectedExamType === 'complete') {
                // Pour l'examen complet, ajouter toutes les tâches
                Object.keys(taskToQuestionNumber).forEach(taskKey => {
                    const questionNumber = taskToQuestionNumber[taskKey];
                    const subject = subjects.find(s => s.criteria.questionNumber === questionNumber);

                    if (subject && responses[taskKey]) {
                        inputData.subjects.push({
                            instruction: subject.data.consigne,
                            answer: responses[taskKey],
                            wordCount: countWords(responses[taskKey]),
                            subjectType: questionNumber
                        });
                    }
                });
            } else {
                // Pour une tâche spécifique, ajouter uniquement cette tâche
                const questionNumber = taskToQuestionNumber[selectedExamType];
                const subject = subjects.find(s => s.criteria.questionNumber === questionNumber);

                if (subject && responses[selectedExamType]) {
                    inputData.subjects.push({
                        instruction: subject.data.consigne,
                        answer: responses[selectedExamType],
                        wordCount: countWords(responses[selectedExamType]),
                        subjectType: questionNumber
                    });
                }
            }

            // Appel à l'API pour évaluer les réponses
            const myHeaders = new Headers();
            myHeaders.append("Content-Type", "application/json");

            const raw = JSON.stringify({
                userId: user.uid,
                inputData: inputData
            });

            const requestOptions = {
                method: 'POST',
                headers: myHeaders,
                body: raw,
                redirect: 'follow'
            };

            const response = await fetch("https://fwabl598k2.execute-api.us-west-1.amazonaws.com/dev/tcfagent/evaluate-batch-tcf-tasks-async", requestOptions);
            const result = await response.json();

            if (result.jobId) {
                setJobId(result.jobId);
            } else {
                throw new Error("Pas de jobId dans la réponse");
            }
        } catch (error) {
            setinProgress(false);
            setOpenToast({
                open: true,
                msg: "Une erreur s'est produite lors de la soumission de vos réponses",
                severity: "error"
            });
            console.error('Error submitting responses:', error);
        }
    };

    // Terminer l'examen
    const completeExam = async () => {
        setIsTimerRunning(false);
        if (timerRef.current) {
            clearInterval(timerRef.current);
        }

        // Soumettre les réponses à l'API
        await submitResponses();

        // Changer le stage après la soumission
        setStage('completed');
    };

    // Fonction pour réinitialiser le module
    const resetModule = () => {
        setStage('checking');
        setSelectedExamType(null);
        setResponses({ task1: '', task2: '', task3: '' });
        setJobId(null);
        setEvaluationResults(null);
        checkSubmissionEligibility();
    };

    // Afficher un chargeur pendant la vérification initiale
    if (stage === 'checking') {
        return (
            <div className='col-12'>
                <Menu />
                <div style={{ marginTop: '8rem' }}></div>
                <MySpinner loading={true} message="Vérification de votre compte..." />
                <Footer />
            </div>
        );
    }

    return (
        <div className='col-12'>
            <Menu />
            <div style={{ marginTop: '8rem' }}></div>
            <div className="tcf-writing-module">
                <ModuleStyle />
                <ModuleContainer>
                    {stage === 'selection' && (
                        <>
                            <div className="remaining-submissions-info">
                                <div className="submissions-badge">
                                    Examens restants : <span>{remainingSubmissions}</span>
                                </div>
                                {subscriptionSource === 'subscription' && (
                                    <div className="subscription-info">
                                        Abonnement actif
                                    </div>
                                )}
                            </div>
                            <TaskSelectionScreen
                                selectedExamType={selectedExamType}
                                setSelectedExamType={setSelectedExamType}
                                startExam={startExam}
                            />
                        </>
                    )}

                    {stage === 'subscription_required' && (
                        <SubscriptionRequired resetModule={resetModule} />
                    )}

                    {stage === 'exam' && (
                        <ExamScreen
                            selectedExamType={selectedExamType}
                            currentTask={currentTask}
                            switchTask={switchTask}
                            timeLeft={timeLeft}
                            responses={responses}
                            updateResponse={updateResponse}
                            instructionsCollapsed={instructionsCollapsed}
                            toggleInstructions={toggleInstructions}
                            showConfirmation={showConfirmation}
                            countWords={countWords}
                            subjects={subjects}
                        />
                    )}

                    {stage === 'completed' && evaluationResults && (
                        <TCFResultsPage
                            resultsData={evaluationResults}
                            resetModule={resetModule}
                            remainingSubmissions={remainingSubmissions}
                            subjects={subjects}
                        />
                    )}

                    {showConfirmationModal && (
                        <ConfirmationModal
                            onCancel={() => setShowConfirmationModal(false)}
                            onConfirm={() => {
                                setShowConfirmationModal(false);
                                completeExam();
                            }}
                        />
                    )}
                </ModuleContainer>
            </div>
            <div style={{ paddingBottom: '5rem' }}></div>
            <MyToast
                openToast={openToast}
                setOpenToast={setOpenToast}
            />

            <MySpinner loading={inProgress} message={spinnerMsg} />

            <Footer />
        </ div>
    );
};

export default TCFWritingModule;